import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import { useGenerationSocket } from '../../../hooks/useGenerationSocket';

interface UseTestGenerationOptions {
  specId: string | undefined;
  onTestsGenerated: () => void;
}

interface UseTestGenerationResult {
  generatingTests: boolean;
  testConsoleOutput: ReturnType<typeof useGenerationSocket>['consoleOutput'];
  showConfirmModal: boolean;
  handleGenerateTests: () => void;
  handleConfirmGeneration: () => void;
  handleCancelGeneration: () => void;
  handleStopGeneration: () => Promise<void>;
}

export function useTestGeneration({ specId, onTestsGenerated }: UseTestGenerationOptions): UseTestGenerationResult {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { generating, consoleOutput, startGeneration, failGeneration, stopGeneration } = useGenerationSocket({
    eventPrefix: 'test-generation',
    schemaId: specId,
    onComplete: (data) => {
      if (data.success) {
        toast.success('Test generation completed successfully');
        onTestsGenerated();
      } else {
        toast.error('Test generation failed');
      }
    },
  });

  const startTestGeneration = useCallback(async () => {
    if (!specId) return;

    startGeneration('Starting test generation from hypotheses...\n');

    try {
      const url = `${API_BASE_URL}/api/generate/tests/${specId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        failGeneration(`Error: ${data.message || 'Failed to start test generation'}`);
        toast.error('Failed to start test generation');
      } else {
        toast.success('Test generation started');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[TestGen] Error starting test generation:', error);
      failGeneration(`Error: ${message}`);
      toast.error('Failed to start test generation');
    }
  }, [specId, startGeneration, failGeneration]);

  const handleGenerateTests = useCallback(() => {
    if (!specId) {
      toast.error('No active specification selected');
      return;
    }
    setShowConfirmModal(true);
  }, [specId]);

  const handleConfirmGeneration = useCallback(() => {
    setShowConfirmModal(false);
    startTestGeneration();
  }, [startTestGeneration]);

  const handleCancelGeneration = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  const handleStopGeneration = useCallback(async () => {
    if (!specId) return;

    try {
      const url = `${API_BASE_URL}/api/generate/tests/${specId}/stop`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Test generation stopped');
        stopGeneration();
      } else {
        toast.error(data.error || 'Failed to stop generation');
      }
    } catch (error: unknown) {
      console.error('[TestGen] Error stopping generation:', error);
      toast.error('Failed to stop generation');
    }
  }, [specId, stopGeneration]);

  return {
    generatingTests: generating,
    testConsoleOutput: consoleOutput,
    showConfirmModal,
    handleGenerateTests,
    handleConfirmGeneration,
    handleCancelGeneration,
    handleStopGeneration,
  };
}
